/**
 * Dealership 360 · Iter-153 · Booking Cancellations & Refunds
 *
 * Three sections:
 *   1. Approvals inbox — cancellations pending reporting-authority review.
 *   2. All cancellations — every request with status, deduction, refund status.
 *   3. Refunds Due queue — approved refunds with SLA countdown (UPI T+5,
 *      Cash T+7, Cheque T+10). Cashier marks refund paid.
 */
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { fmtINR, fmtDate } from "@/lib/format";
import { AlertTriangle, CheckCircle2, XCircle, RefreshCw, Wallet, Timer } from "lucide-react";

const STATUS_TONE = {
  pending: "text-amber-600", approved: "text-emerald-600", rejected: "text-red-500",
  not_initiated: "text-muted-foreground", queued: "text-amber-600",
  initiated: "text-blue-500", paid_out: "text-emerald-600", failed: "text-red-500",
};

function daysUntil(iso) {
  if (!iso) return null;
  const diff = new Date(iso).getTime() - Date.now();
  return Math.ceil(diff / (24 * 3600 * 1000));
}

// ---------------- Refunds Due (SLA countdown) ----------------
function RefundsDuePanel({ onChange }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [payTarget, setPayTarget] = useState(null);
  const [payRef, setPayRef] = useState("");

  const load = async () => {
    setBusy(true);
    try { setRows((await api.get("/dealership/refunds-due")).data || []); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const markPaid = async () => {
    try {
      await api.post(`/dealership/cancellations/${payTarget.id}/refund-paid`,
        { reference: payRef });
      toast.success("Refund paid — ledger update queued");
      setPayTarget(null); setPayRef("");
      load(); onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-3" data-testid="refunds-due-panel">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-accent flex items-center gap-1"><Wallet className="w-3 h-3" /> Refunds Due</div>
          <div className="text-[11px] text-muted-foreground">
            Approved cancellations awaiting physical handout / bank transfer.
            SLA — UPI/Online <b>T+5</b> · Cash <b>T+7</b> · Cheque/DD <b>T+10</b>.
          </div>
        </div>
        <Button size="sm" variant="ghost" onClick={load} className="rounded-sm h-8 gap-1" data-testid="refunds-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>
      <div className="card-sharp overflow-hidden">
        <table className="w-full dense">
          <thead><tr>
            <th>Cancellation</th><th>Booking#</th><th>Customer</th>
            <th className="text-right">Refund</th><th>Mode</th><th>SLA</th>
            <th>Status</th><th></th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan="8" className="text-center py-10 text-muted-foreground">No refunds pending. Clean queue ✓</td></tr>}
            {rows.map((c) => {
              const days = daysUntil(c.refund_sla_deadline);
              const overdue = days !== null && days < 0;
              const dueSoon = days !== null && days >= 0 && days <= 2;
              return (
                <tr key={c.id} data-testid={`refund-row-${c.booking_number}`}>
                  <td className="font-mono text-[10px] text-muted-foreground">{c.id.slice(0, 8)}</td>
                  <td className="font-mono text-xs">{c.booking_number}</td>
                  <td className="text-xs">{c.customer?.first_name} {c.customer?.last_name}</td>
                  <td className="text-right tabular-nums font-medium">{fmtINR(c.final_refund_amount)}</td>
                  <td className="text-[10px] font-mono uppercase">{c.refund_mode}</td>
                  <td className={`text-[10px] font-mono ${overdue ? "text-red-500" : dueSoon ? "text-amber-600" : "text-muted-foreground"}`}>
                    <Timer className="w-3 h-3 inline mr-0.5" />
                    {days === null ? "—" : days < 0 ? `${Math.abs(days)}d OVERDUE` : days === 0 ? "TODAY" : `T+${days}d`}
                    <div className="text-[9px] opacity-70">{fmtDate(c.refund_sla_deadline)}</div>
                  </td>
                  <td><span className={`text-[10px] font-mono uppercase ${STATUS_TONE[c.refund_status] || ""}`}>{c.refund_status}</span></td>
                  <td className="text-right">
                    <Button size="sm" onClick={() => { setPayTarget(c); setPayRef(""); }}
                      className="h-7 rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] gap-1"
                      data-testid={`refund-pay-${c.booking_number}`}>
                      <CheckCircle2 className="w-3 h-3" /> Mark Paid
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {/* Mark paid sheet */}
      <Sheet open={!!payTarget} onOpenChange={(v) => !v && setPayTarget(null)}>
        <SheetContent side="right" className="w-full sm:max-w-md rounded-none space-y-3">
          <SheetHeader>
            <SheetTitle className="font-heading">Mark Refund Paid</SheetTitle>
            <SheetDescription>Capture the payout reference (UPI txn ID / cheque number / cash voucher). Ledger receipt reversal is auto-posted.</SheetDescription>
          </SheetHeader>
          {payTarget && (
            <div className="space-y-3">
              <div className="text-xs">
                <div>Booking <span className="font-mono">{payTarget.booking_number}</span></div>
                <div>Refund <span className="font-mono">{fmtINR(payTarget.final_refund_amount)}</span> · via <span className="font-mono uppercase">{payTarget.refund_mode}</span></div>
              </div>
              <div><Label>Payment reference *</Label>
                <Input value={payRef} onChange={(e) => setPayRef(e.target.value)}
                  placeholder="UPI txn / cheque # / cash voucher" className="rounded-sm mt-1 font-mono" data-testid="refund-pay-ref" />
              </div>
              <Button onClick={markPaid} disabled={!payRef.trim()}
                className="w-full rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="refund-pay-save">
                Confirm Refund Paid
              </Button>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

// ---------------- Approvals + All list ----------------
export default function CancellationsTab() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [act, setAct] = useState(null);     // { row, action }
  const [comment, setComment] = useState("");
  const [override, setOverride] = useState("");

  const load = async () => {
    setBusy(true);
    try { setRows((await api.get("/dealership/cancellations")).data || []); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const pending = useMemo(() => rows.filter((r) => r.status === "pending"), [rows]);
  const closed  = useMemo(() => rows.filter((r) => r.status !== "pending"), [rows]);

  const perform = async () => {
    try {
      const payload = { reviewer_comment: comment };
      if (act.action === "approve" && override) payload.override_deduction = Number(override);
      await api.post(`/dealership/cancellations/${act.row.id}/${act.action}`, payload);
      toast.success(act.action === "approve" ? "Cancellation approved — refund queued" : "Cancellation rejected");
      setAct(null); setComment(""); setOverride("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="cancellations-tab">
      <Tabs defaultValue="approvals">
        <TabsList className="rounded-sm">
          <TabsTrigger value="approvals" className="rounded-sm" data-testid="tab-cancel-approvals">
            Approvals {pending.length > 0 && <span className="ml-1 text-[10px] font-mono px-1 rounded-sm bg-red-600 text-white">{pending.length}</span>}
          </TabsTrigger>
          <TabsTrigger value="all" className="rounded-sm" data-testid="tab-cancel-all">All cancellations</TabsTrigger>
          <TabsTrigger value="refunds" className="rounded-sm" data-testid="tab-cancel-refunds">Refunds Due</TabsTrigger>
        </TabsList>

        <TabsContent value="approvals" className="mt-3">
          <div className="text-[11px] text-muted-foreground mb-2">
            Reporting-authority queue. Post-allocation cancellations and beyond-15-day slabs always land here.
          </div>
          <div className="card-sharp overflow-hidden">
            <table className="w-full dense">
              <thead><tr>
                <th>Requested</th><th>Booking#</th><th>Customer</th>
                <th>Reason</th><th className="text-right">Paid</th>
                <th className="text-right">Deduction</th><th className="text-right">Refund</th>
                <th>Days since</th><th></th>
              </tr></thead>
              <tbody>
                {pending.length === 0 && <tr><td colSpan="9" className="text-center py-10 text-muted-foreground">No cancellations awaiting approval ✓</td></tr>}
                {pending.map((r) => (
                  <tr key={r.id} data-testid={`cancel-row-${r.booking_number}`}>
                    <td className="text-[10px] font-mono text-muted-foreground">{fmtDate(r.requested_at)}</td>
                    <td className="font-mono text-xs">{r.booking_number}</td>
                    <td className="text-xs">{r.customer?.first_name} {r.customer?.last_name}</td>
                    <td className="text-xs max-w-[200px] truncate" title={r.reason}>{r.reason}</td>
                    <td className="text-right tabular-nums">{fmtINR(r.computed_total_paid)}</td>
                    <td className="text-right tabular-nums text-amber-700">{fmtINR(r.proposed_deduction_amount)}<div className="text-[9px] font-mono opacity-70">{r.computed_deduction_pct}%</div></td>
                    <td className="text-right tabular-nums font-medium text-emerald-700">{fmtINR(r.proposed_refund_amount)}</td>
                    <td className="text-[10px] font-mono">{r.computed_days_since_booking}d</td>
                    <td className="text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-emerald-700"
                        onClick={() => { setAct({ row: r, action: "approve" }); setOverride(String(r.proposed_deduction_amount || "")); }}
                        data-testid={`cancel-approve-${r.booking_number}`}>
                        <CheckCircle2 className="w-3 h-3" /> Approve
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-red-600"
                        onClick={() => setAct({ row: r, action: "reject" })}
                        data-testid={`cancel-reject-${r.booking_number}`}>
                        <XCircle className="w-3 h-3" /> Reject
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="all" className="mt-3">
          <div className="flex items-center gap-2 mb-2">
            <div className="text-[11px] text-muted-foreground flex-1">Every cancellation ever raised — history + refund status.</div>
            <Button size="sm" variant="ghost" onClick={load} className="rounded-sm h-7 gap-1"><RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} /> Refresh</Button>
          </div>
          <div className="card-sharp overflow-hidden">
            <table className="w-full dense">
              <thead><tr>
                <th>Requested</th><th>Booking#</th><th>Customer</th>
                <th>Reason</th><th className="text-right">Refund</th>
                <th>Refund status</th><th>SLA</th><th>Cancel status</th>
              </tr></thead>
              <tbody>
                {rows.length === 0 && <tr><td colSpan="8" className="text-center py-10 text-muted-foreground">No cancellations yet.</td></tr>}
                {rows.map((r) => {
                  const days = daysUntil(r.refund_sla_deadline);
                  return (
                    <tr key={r.id} data-testid={`cancel-list-row-${r.booking_number}`}>
                      <td className="text-[10px] font-mono text-muted-foreground">{fmtDate(r.requested_at)}</td>
                      <td className="font-mono text-xs">{r.booking_number}</td>
                      <td className="text-xs">{r.customer?.first_name} {r.customer?.last_name}</td>
                      <td className="text-xs max-w-[220px] truncate" title={r.reason}>{r.reason}</td>
                      <td className="text-right tabular-nums">{fmtINR(r.final_refund_amount || r.proposed_refund_amount)}</td>
                      <td><span className={`text-[10px] font-mono uppercase ${STATUS_TONE[r.refund_status] || ""}`}>{r.refund_status}</span></td>
                      <td className="text-[10px] font-mono text-muted-foreground">{days === null ? "—" : days < 0 ? `${Math.abs(days)}d late` : `T+${days}d`}</td>
                      <td><span className={`text-[10px] font-mono uppercase ${STATUS_TONE[r.status] || ""}`}>{r.status}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="refunds" className="mt-3">
          <RefundsDuePanel onChange={load} />
        </TabsContent>
      </Tabs>

      {/* Approve / Reject sheet */}
      <Sheet open={!!act} onOpenChange={(v) => !v && setAct(null)}>
        <SheetContent side="right" className="w-full sm:max-w-md rounded-none space-y-3">
          <SheetHeader>
            <SheetTitle className="font-heading">
              {act?.action === "approve" ? "Approve Cancellation" : "Reject Cancellation"}
            </SheetTitle>
            <SheetDescription>
              {act?.action === "approve"
                ? "Approving cancels the booking, releases the allocated VIN back to stock, and queues the refund."
                : "Rejecting keeps the booking active. Refund is not initiated."}
            </SheetDescription>
          </SheetHeader>
          {act?.row && (
            <div className="space-y-3 text-xs">
              <div className="border rounded-sm p-2 bg-slate-50/60">
                <div>Booking <b>{act.row.booking_number}</b></div>
                <div>Customer <b>{act.row.customer?.first_name} {act.row.customer?.last_name}</b></div>
                <div>Total paid <span className="font-mono">{fmtINR(act.row.computed_total_paid)}</span></div>
                <div>Proposed deduction <span className="font-mono">{fmtINR(act.row.proposed_deduction_amount)} ({act.row.computed_deduction_pct}%)</span></div>
                <div>Days since booking <span className="font-mono">{act.row.computed_days_since_booking}</span></div>
                <div className="mt-1 opacity-70 italic">"{act.row.reason}"</div>
              </div>

              {act.action === "approve" && (
                <div>
                  <Label className="text-xs">Override deduction (₹)</Label>
                  <Input type="number" value={override} onChange={(e) => setOverride(e.target.value)}
                    className="rounded-sm mt-1 font-mono" data-testid="cancel-override-deduction" />
                  <div className="text-[10px] text-muted-foreground mt-1">
                    Leave as-is to accept the config-driven slab. Reduce or increase to override — must be ≤ total paid.
                  </div>
                </div>
              )}

              <div>
                <Label className="text-xs">Reviewer comment</Label>
                <Textarea value={comment} onChange={(e) => setComment(e.target.value)}
                  rows={3} className="rounded-sm mt-1" data-testid="cancel-reviewer-comment"
                  placeholder="Rationale for approval / rejection…" />
              </div>

              <Button onClick={perform}
                className={`w-full rounded-sm text-white ${act.action === "approve" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"}`}
                data-testid="cancel-action-save">
                {act.action === "approve" ? "Approve & Queue Refund" : "Reject"}
              </Button>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
